import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { snakeCase } from "lodash";

import { getOutcomesTourProgress, productAnalytics, updateOutcomesTourProgress } from "utils";

import {
  OUTCOMES_TOUR_CHAPTER_ORDER,
  OUTCOMES_TOUR_ELEMENT_ATTRIBUTE,
  OUTCOMES_TOUR_MIN_PLANS_FOR_RESULTS,
  OUTCOMES_TOUR_OVERLAY_SELECTOR,
  OUTCOMES_TOUR_STEPS,
  OUTCOMES_TOUR_TAB_START_DELAY_MS,
  OutcomesTourChapter,
  OutcomesTourKind,
  OutcomesTourStep,
  ScenarioAnalysisTab,
} from "consts";

import { hasOpenOverlay, useIsOverlayOpen } from "pages/app/scenario-analysis/hooks/useIsOverlayOpen";

interface OutcomesTourRun {
  kind: OutcomesTourKind;
  steps: readonly OutcomesTourStep[];
  /** The step on screen; null until the first step's tab has opened. */
  index: number | null;
  /** A step on another tab, waiting for that tab to open. A leave-guard prompt can hold it back. */
  pendingIndex: number | null;
}

/** What the tour card shows and does, or null while no step is on screen. */
export interface OutcomesTourCard {
  step: OutcomesTourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  /** The last step's area was used, such as Add scenario: the tour ends and the click carries on. */
  onLastAreaUsed: () => void;
  onDismiss: () => void;
  onStepAway: () => void;
}

export interface UseSoilSightOutcomesTourOptions {
  activeTab: ScenarioAnalysisTab;
  goToTab: (tab: ScenarioAnalysisTab) => void;
  planCount: number;
  /** False while the figures are still settling, so the tour does not open over a loader. */
  isReady: boolean;
  /** Takes focus back when the tour finishes or is dismissed. */
  returnFocus: () => void;
}

type CloseOutcome = "finished" | "usedLastArea" | "dismissed" | "steppedAway";

// "arrival" resolves to whichever tab is on screen once the page is ready.
type PendingChapter = ScenarioAnalysisTab | "arrival";

const OVERLAY_OPTIONS = { selector: OUTCOMES_TOUR_OVERLAY_SELECTOR, ignoreWithin: `[${OUTCOMES_TOUR_ELEMENT_ATTRIBUTE}]` };

const tourKindFor = (planCount: number): OutcomesTourKind => (planCount >= OUTCOMES_TOUR_MIN_PLANS_FOR_RESULTS ? "results" : "empty");

const chaptersOf = (steps: readonly OutcomesTourStep[]): OutcomesTourChapter[] => Array.from(new Set(steps.map(({ chapter }) => chapter)));

/** The unseen chapter for `tab` and every later unseen one, or null when that tab's chapter is not due. */
const pickUpSteps = (kind: OutcomesTourKind, tab: ScenarioAnalysisTab): OutcomesTourStep[] | null => {
  const { dismissed, seenChapters } = getOutcomesTourProgress(kind);
  const steps = OUTCOMES_TOUR_STEPS[kind];
  if (dismissed || seenChapters.includes(tab) || !steps.some(({ chapter }) => chapter === tab)) return null;
  const laterChapters = OUTCOMES_TOUR_CHAPTER_ORDER.slice(OUTCOMES_TOUR_CHAPTER_ORDER.indexOf(tab)).filter((chapter) => !seenChapters.includes(chapter));
  return steps.filter(({ chapter }) => laterChapters.includes(chapter));
};

/** Moves to a step: shown now on its own tab, or held until the tab it needs has opened. */
const enterStep = (run: OutcomesTourRun, index: number, activeTab: ScenarioAnalysisTab) => {
  const { chapter } = run.steps[index];
  if (chapter === activeTab) return { run: { ...run, index, pendingIndex: null }, tabToOpen: null };
  return { run: { ...run, pendingIndex: index }, tabToOpen: chapter };
};

/** Runs the results-page tour. Only arrival, a tab-bar choice and "Show me around" start it; tabs opened by actions never do. */
export const useSoilSightOutcomesTour = ({ activeTab, goToTab, planCount, isReady, returnFocus }: UseSoilSightOutcomesTourOptions) => {
  const [run, setRun] = useState<OutcomesTourRun | null>(null);
  const [pendingChapter, setPendingChapter] = useState<PendingChapter | null>(null);
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOverlayOpen = useIsOverlayOpen({ enabled: run !== null || pendingChapter !== null, ...OVERLAY_OPTIONS });

  const cancelPendingStart = useCallback(() => {
    if (startTimerRef.current) clearTimeout(startTimerRef.current);
    startTimerRef.current = null;
  }, []);

  const startAfterDelay = useCallback(
    (chapter: PendingChapter) => {
      cancelPendingStart();
      startTimerRef.current = setTimeout(() => setPendingChapter(chapter), OUTCOMES_TOUR_TAB_START_DELAY_MS);
    },
    [cancelPendingStart]
  );

  // Arrival waits too, so a modal mounted with the page (the onboarding hand-off) is seen as open first.
  useEffect(() => {
    startAfterDelay("arrival");
    return cancelPendingStart;
  }, [startAfterDelay, cancelPendingStart]);

  useEffect(() => {
    // The live check covers the render before the overlay watcher has reported.
    if (!pendingChapter || !isReady || isOverlayOpen || run || hasOpenOverlay(OUTCOMES_TOUR_OVERLAY_SELECTOR)) return;
    setPendingChapter(null);
    // An action moved the page on before the chapter was due, so it waits for its own tab.
    if (pendingChapter !== "arrival" && pendingChapter !== activeTab) return;
    const kind = tourKindFor(planCount);
    const steps = pickUpSteps(kind, activeTab);
    if (!steps?.length) return;
    setRun({ kind, steps, index: 0, pendingIndex: null });
    productAnalytics.track("soilsights:tour_start", { start_type: "auto" });
  }, [pendingChapter, isReady, isOverlayOpen, run, activeTab, planCount]);

  const close = useCallback(
    (outcome: CloseOutcome) => {
      if (!run) return;
      const lastStepId = run.index === null ? null : run.steps[run.index].id;
      productAnalytics.track("soilsights:tour_end", { outcome: snakeCase(outcome), last_step: lastStepId && snakeCase(lastStepId) });
      if (outcome === "dismissed") {
        updateOutcomesTourProgress(run.kind, (progress) => ({ ...progress, dismissed: true }));
      } else {
        const isComplete = outcome === "finished" || outcome === "usedLastArea";
        const shown = isComplete ? run.steps : run.steps.slice(0, run.index === null ? 0 : run.index + 1);
        updateOutcomesTourProgress(run.kind, (progress) => ({
          ...progress,
          seenChapters: Array.from(new Set([...progress.seenChapters, ...chaptersOf(shown)])),
        }));
      }
      setRun(null);
      // Stepping away leaves focus where the farmer put it, and using the last area leaves it with what that opened.
      if (outcome === "finished" || outcome === "dismissed") returnFocus();
    },
    [run, returnFocus]
  );

  useEffect(() => {
    if (run && isOverlayOpen) close("steppedAway");
  }, [run, isOverlayOpen, close]);

  // Before paint, so the card never draws a step against a tab that has just gone.
  useLayoutEffect(() => {
    if (!run) return;
    if (run.pendingIndex !== null) {
      if (run.steps[run.pendingIndex].chapter === activeTab) setRun({ ...run, index: run.pendingIndex, pendingIndex: null });
      return;
    }
    // A tab change the tour did not make (the keyboard, an action) steps away, as a click elsewhere would.
    if (run.index !== null && run.steps[run.index].chapter !== activeTab) close("steppedAway");
  }, [run, activeTab, close]);

  const goToStep = useCallback(
    (index: number) => {
      if (!run || run.pendingIndex !== null || index < 0 || index >= run.steps.length) return;
      const entered = enterStep(run, index, activeTab);
      setRun(entered.run);
      if (entered.tabToOpen) goToTab(entered.tabToOpen);
    },
    [run, activeTab, goToTab]
  );

  /** A tab-bar choice. The chapter starts shortly after the tab opens, if it is still due then. */
  const onTabBarSelect = useCallback((tab: ScenarioAnalysisTab) => startAfterDelay(tab), [startAfterDelay]);

  /** "Show me around": the whole tour for the current state from step 1, whatever was seen or dismissed. */
  const showMeAround = useCallback(() => {
    const kind = tourKindFor(planCount);
    cancelPendingStart();
    setPendingChapter(null);
    const entered = enterStep({ kind, steps: OUTCOMES_TOUR_STEPS[kind], index: null, pendingIndex: null }, 0, activeTab);
    setRun(entered.run);
    productAnalytics.track("soilsights:tour_start", { start_type: "manual" });
    if (entered.tabToOpen) goToTab(entered.tabToOpen);
  }, [planCount, activeTab, goToTab, cancelPendingStart]);

  const card = useMemo<OutcomesTourCard | null>(() => {
    if (!run || run.index === null) return null;
    const { index } = run;
    return {
      step: run.steps[index],
      stepIndex: index,
      totalSteps: run.steps.length,
      onNext: () => goToStep(index + 1),
      onBack: () => goToStep(index - 1),
      onFinish: () => close("finished"),
      onLastAreaUsed: () => close("usedLastArea"),
      onDismiss: () => close("dismissed"),
      onStepAway: () => close("steppedAway"),
    };
  }, [run, goToStep, close]);

  return { card, onTabBarSelect, showMeAround };
};
